import { useEffect, useRef, useState, useMemo } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useNavigate } from 'react-router-dom';
import { useMapboxToken } from '@/hooks/useMapboxToken';
import { MapPropertyCard } from './MapPropertyCard';
import { Loader2, MapPinOff } from 'lucide-react';
import { useCurrency } from '@/contexts/CurrencyContext';
import { formatPrice as fmtPrice, formatPriceCompact } from '@/lib/formatPrice';
import { MapFilterBar } from './MapFilterBar';
import type { FilterValues } from './PropertyFilters';

interface PropertyMapViewProps {
  properties: any[];
  isLoading?: boolean;
  filters?: FilterValues;
  onFiltersChange?: (next: FilterValues) => void;
  totalCount?: number;
}

// Default to Addis Ababa
const DEFAULT_CENTER: [number, number] = [38.7578, 9.0192];
const DEFAULT_ZOOM = 11;


export function PropertyMapView({ properties, isLoading, filters, onFiltersChange, totalCount }: PropertyMapViewProps) {
  const { data: token, isLoading: tokenLoading, error: tokenError } = useMapboxToken();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<Map<string, mapboxgl.Marker>>(new Map());
  const popupRef = useRef<mapboxgl.Popup | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { displayCurrency, rate } = useCurrency();

  const geoProps = useMemo(
    () =>
      properties.filter(
        (p) =>
          typeof p.latitude === 'number' &&
          typeof p.longitude === 'number' &&
          !isNaN(p.latitude) &&
          !isNaN(p.longitude),
      ),
    [properties],
  );

  // Init map
  useEffect(() => {
    if (!token || !containerRef.current || mapRef.current) return;
    if (!token.startsWith('pk.')) return;
    mapboxgl.accessToken = token;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      attributionControl: true,
    });
    map.addControl(new mapboxgl.NavigationControl({ visualizePitch: false }), 'top-right');
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [token]);

  // Render price-pill markers + fit bounds
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const apply = () => {
      // Clear existing markers
      markersRef.current.forEach((m) => m.remove());
      markersRef.current.clear();

      if (geoProps.length === 0) return;

      const bounds = new mapboxgl.LngLatBounds();

      geoProps.forEach((p) => {
        const el = document.createElement('div');
        el.className =
          'mapbox-price-pill cursor-pointer select-none px-2.5 py-1 rounded-full border-2 border-white text-[11px] font-bold shadow-md whitespace-nowrap transition-transform hover:scale-110';
        const isFeatured = p.featured_tier === 'hot' || p.featured_tier === 'super_hot' || p.is_featured;
        el.style.background = isFeatured ? 'hsl(215, 65%, 38%)' : '#ffffff';
        el.style.color = isFeatured ? '#ffffff' : 'hsl(215, 65%, 38%)';
        el.textContent = formatPriceCompact(p.price, p.currency || 'ETB', displayCurrency, rate);
        el.dataset.id = p.id;

        el.addEventListener('mouseenter', () => setHoveredId(p.id));
        el.addEventListener('mouseleave', () => setHoveredId(null));
        el.addEventListener('click', (e) => {
          e.stopPropagation();
          openPopup(p);
        });

        const marker = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
          .setLngLat([p.longitude, p.latitude])
          .addTo(map);

        markersRef.current.set(p.id, marker);
        bounds.extend([p.longitude, p.latitude]);
      });

      if (!bounds.isEmpty()) {
        map.fitBounds(bounds, { padding: 60, maxZoom: 14, duration: 600 });
      }
    };

    if (map.isStyleLoaded()) apply();
    else map.once('load', apply);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geoProps, displayCurrency, rate]);

  // Highlight hovered pin
  useEffect(() => {
    markersRef.current.forEach((marker, id) => {
      const el = marker.getElement();
      if (id === hoveredId) {
        el.style.transform = (el.style.transform || '') + ' scale(1.2)';
        el.style.zIndex = '10';
      } else {
        el.style.transform = el.style.transform.replace(/\s?scale\([^)]+\)/, '');
        el.style.zIndex = '';
      }
    });
  }, [hoveredId]);

  const openPopup = (p: any) => {
    const map = mapRef.current;
    if (!map) return;
    popupRef.current?.remove();

    const title = typeof p.title === 'object' ? p.title.en || Object.values(p.title)[0] : p.title;
    const img =
      (p.property_images?.find((i: any) => i.is_primary) || p.property_images?.[0])?.url ||
      'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=400&h=300&fit=crop';
    const slug = p.slug || p.id;
    const price = fmtPrice(p.price, p.currency || 'ETB', displayCurrency, rate);

    const html = `
      <div class="w-56" style="font-family: inherit">
        <img src="${img}" alt="" style="width:100%; height:120px; object-fit:cover; border-radius:6px"/>
        <div style="padding:8px 4px 4px">
          <div style="font-weight:700; color:hsl(215,65%,38%); font-size:14px">${price}</div>
          <div style="font-size:12px; font-weight:600; margin-top:2px; line-height:1.3">${title || ''}</div>
          <div style="font-size:10px; color:#666; margin-top:2px">${p.city || ''}</div>
          <a href="/property/${slug}" data-nav="${slug}" style="display:block; margin-top:6px; text-align:center; background:hsl(215,65%,38%); color:white; padding:6px; border-radius:4px; text-decoration:none; font-size:11px; font-weight:600">View Details</a>
        </div>
      </div>
    `;

    const popup = new mapboxgl.Popup({ offset: 28, closeButton: true, maxWidth: '240px' })
      .setLngLat([p.longitude, p.latitude])
      .setHTML(html)
      .addTo(map);

    // Intercept the View Details link for SPA navigation
    setTimeout(() => {
      const link = popup.getElement().querySelector<HTMLAnchorElement>('a[data-nav]');
      if (link) {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          popup.remove();
          navigate(`/property/${link.dataset.nav}`);
        });
      }
    }, 0);

    popupRef.current = popup;
  };

  // Loading / error states
  if (tokenError) {
    const message = tokenError instanceof Error ? tokenError.message : 'Failed to load map. Please refresh.';
    return (
      <div className="h-[calc(100vh-220px)] flex items-center justify-center bg-muted/30 rounded-lg border">
        <p className="max-w-md px-4 text-center text-sm text-muted-foreground">{message}</p>
      </div>
    );
  }

  return (
    <div>
      {filters && onFiltersChange && (
        <MapFilterBar filters={filters} onChange={onFiltersChange} resultCount={totalCount} />
      )}
      <div className="grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-4 h-[calc(100vh-260px)] min-h-[600px]">
      {/* Left: scrollable list of cards */}
      <div className="overflow-y-auto pr-1 space-y-2 hidden lg:block">
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}
        {!isLoading && properties.length === 0 && (
          <div className="text-center py-12 text-sm text-muted-foreground">No properties to display.</div>
        )}
        {!isLoading && geoProps.length === 0 && properties.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900 flex gap-2">
            <MapPinOff className="h-4 w-4 shrink-0" />
            <span>None of the matching properties have coordinates yet. They appear in List view.</span>
          </div>
        )}
        {properties.map((p) => (
          <MapPropertyCard
            key={p.id}
            property={p}
            active={hoveredId === p.id}
            onHover={setHoveredId}
          />
        ))}
      </div>

      {/* Right: Mapbox */}
      <div className="relative rounded-lg overflow-hidden border bg-muted/20 h-full">
        {(tokenLoading || !token) && (
          <div className="absolute inset-0 flex items-center justify-center bg-muted/40 z-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}
        <div ref={containerRef} className="absolute inset-0" />
      </div>
      </div>
    </div>
  );
}
